import { AmplitudeEvent } from "@common-shared/config/amplitude.config"
import { COMMON_COVERAGE_HISTORY_CONFIG } from "@common-shared/config/coverageHistory.config"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import type { IPagination } from "@common-shared/types/general.types"
import {
    ICoverageHistoryAggregationsFlags,
    ICoverageHistoryAggregationsMedicaidActivePlanType,
    ICoverageHistoryAggregationsMedicaidPlanType,
    ICoverageHistoryAggregationsStatusCodes,
    ICoverageHistoryBaseFilters,
    ICoverageHistoryListSort,
} from "@common-shared/types/internalApis/coverageHistory.types"
import { IPoliciesListFiltersData } from "@common-shared/types/policies.types"
import {
    IOneVerifyGetAgenticListAdditionalRequestData,
    IOneVerifyGetAgenticListRequestData,
    IOneVerifyGetHistoryListAdditionalData,
    IOneVerifyGetHistoryListRequestData,
    IOneVerifyHistoryGetSingleRequestData,
    IOneVerifyWriteToEhrData,
} from "@common-shared/types/requestData/oneVerifyRequestData.types"
import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types"
import {
    ICoverageHistoryAggregationsPlanYearResets,
    IOneVerifyCoverageCheckHistory,
    IOneVerifyCoverageResult,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { API_ROUTES } from "../config/routes.config"
import { AmplitudeHelper } from "../helpers/amplitude.helper"
import { HttpHelper } from "../helpers/http.helper"
import { PoliciesHelper } from "../helpers/policies.helper"
import { UtilHelper } from "../helpers/util.helper"
import type { ICoverageHistoryListResult } from "../types/data.types"

export class PoliciesService {
    /**
     * Get list of list of policies for reports
     */
    static async getReportsList(
        practice: IPractice,
        pagination: IPagination,
        filters: IPoliciesListFiltersData = {},
        sort: ICoverageHistoryListSort = {},
        abortSignal?: AbortSignal,
        additionalRequestData: IOneVerifyGetHistoryListAdditionalData = {},
    ): Promise<ICoverageHistoryListResult | undefined> {
        AmplitudeHelper.logEvent(AmplitudeEvent.RAN_REPORT, {
            practiceId: practice.id,
            practiceName: practice.name,
            filterParams: PoliciesHelper.redactSensitiveFiltersData(filters),
            reportType: PoliciesListType.REPORTS,
        })

        return await PoliciesService.getPoliciesList(
            API_ROUTES.COVERAGE_REPORTS_GET_LIST,
            practice,
            PoliciesService.mapGetHistoryListRequestData(
                PoliciesListType.REPORTS,
                pagination,
                filters,
                sort,
                additionalRequestData,
            ),
            abortSignal,
        )
    }

    /**
     * Get list of historical policies - Search in UI
     */
    static async getSearchList(
        practice: IPractice,
        pagination: IPagination,
        filters: IPoliciesListFiltersData = {},
        sort: ICoverageHistoryListSort = {},
        abortSignal?: AbortSignal,
        additionalRequestData: IOneVerifyGetHistoryListAdditionalData = {},
    ): Promise<ICoverageHistoryListResult | undefined> {
        AmplitudeHelper.logEvent(AmplitudeEvent.RAN_REPORT, {
            practiceId: practice.id,
            practiceName: practice.name,
            filterParams: PoliciesHelper.redactSensitiveFiltersData(filters),
            reportType: PoliciesListType.HISTORY_SEARCH,
        })

        return await PoliciesService.getPoliciesList(
            API_ROUTES.COVERAGE_HISTORY_GET_LIST,
            practice,
            PoliciesService.mapGetHistoryListRequestData(
                PoliciesListType.HISTORY_SEARCH,
                pagination,
                filters,
                sort,
                additionalRequestData,
            ),
            abortSignal,
        )
    }

    /**
     * Get list of assigned tasks
     */
    static async getAssignedTasksList(
        practice: IPractice,
        listType: PoliciesListType,
        pagination: IPagination,
        filters: IPoliciesListFiltersData = {},
        sort: ICoverageHistoryListSort = {},
        abortSignal?: AbortSignal,
        additionalRequestData: IOneVerifyGetHistoryListAdditionalData = {},
    ): Promise<ICoverageHistoryListResult | undefined> {
        AmplitudeHelper.logEvent(AmplitudeEvent.ASSIGNED_POLICIES_PAGE_VIEWED, {
            practiceId: practice.id,
            practiceName: practice.name,
            filterParams: PoliciesHelper.redactSensitiveFiltersData(filters),
            reportType: listType,
        })

        return await PoliciesService.getPoliciesList(
            API_ROUTES.TASK_ASSIGNMENTS_GET_LIST,
            practice,
            PoliciesService.mapGetHistoryListRequestData(
                listType,
                pagination,
                filters,
                sort,
                additionalRequestData,
            ),
            abortSignal,
        )
    }

    /**
     * Get list of overridden policies
     */
    static async getOverridesList(
        practice: IPractice,
        pagination: IPagination,
        filters: IPoliciesListFiltersData = {},
        sort: ICoverageHistoryListSort = {},
        abortSignal?: AbortSignal,
        additionalRequestData: IOneVerifyGetHistoryListAdditionalData = {},
    ): Promise<ICoverageHistoryListResult | undefined> {
        AmplitudeHelper.logEvent(AmplitudeEvent.RAN_REPORT, {
            practiceId: practice.id,
            practiceName: practice.name,
            filterParams: PoliciesHelper.redactSensitiveFiltersData(filters),
            reportType: PoliciesListType.OVERRIDES,
        })

        return await PoliciesService.getPoliciesList(
            API_ROUTES.COVERAGE_OVERRIDES_GET_LIST,
            practice,
            PoliciesService.mapGetHistoryListRequestData(
                PoliciesListType.OVERRIDES,
                pagination,
                filters,
                sort,
                additionalRequestData,
            ),
            abortSignal,
        )
    }

    /**
     * Get list of plan year resets policies
     */
    static async getPlanYearResetsList(
        practice: IPractice,
        pagination: IPagination,
        filters: IPoliciesListFiltersData = {},
        sort: ICoverageHistoryListSort = {},
        abortSignal?: AbortSignal,
        additionalRequestData: IOneVerifyGetHistoryListAdditionalData = {},
    ): Promise<ICoverageHistoryListResult | undefined> {
        AmplitudeHelper.logEvent(AmplitudeEvent.RAN_REPORT, {
            practiceId: practice.id,
            practiceName: practice.name,
            filterParams: PoliciesHelper.redactSensitiveFiltersData(filters),
            reportType: PoliciesListType.PLAN_YEAR_RESETS,
        })

        return await PoliciesService.getPoliciesList(
            API_ROUTES.COVERAGE_PLAN_YEAR_RESETS_GET_LIST,
            practice,
            PoliciesService.mapGetHistoryListRequestData(
                PoliciesListType.PLAN_YEAR_RESETS,
                pagination,
                filters,
                sort,
                additionalRequestData,
            ),
            abortSignal,
        )
    }

    /**
     * Get list of list of policies for medicaid
     */
    static async getMedicaidList(
        practice: IPractice,
        pagination: IPagination,
        filters: IPoliciesListFiltersData = {},
        sort: ICoverageHistoryListSort = {},
        abortSignal?: AbortSignal,
        additionalRequestData: IOneVerifyGetHistoryListAdditionalData = {},
    ): Promise<ICoverageHistoryListResult | undefined> {
        AmplitudeHelper.logEvent(AmplitudeEvent.RAN_REPORT, {
            practiceId: practice.id,
            practiceName: practice.name,
            filterParams: PoliciesHelper.redactSensitiveFiltersData(filters),
            reportType: PoliciesListType.MEDICAID,
        })

        return await PoliciesService.getPoliciesList(
            API_ROUTES.COVERAGE_MEDICAID_GET_LIST,
            practice,
            PoliciesService.mapGetHistoryListRequestData(
                PoliciesListType.MEDICAID,
                pagination,
                filters,
                sort,
                additionalRequestData,
            ),
            abortSignal,
        )
    }

    /**
     * Get aggregations for medicaid report by plan type
     */
    static async getMedicaidReportsAggregationPlanType(
        practice: IPractice,
        filters: IPoliciesListFiltersData = {},
        abortSignal?: AbortSignal,
    ): Promise<ICoverageHistoryAggregationsMedicaidPlanType | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_MEDICAID_GET_AGGREGATION_PLAN_TYPE.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            {
                ...PoliciesService.mapPoliciesFilters(
                    PoliciesListType.MEDICAID,
                    filters,
                ),
            },
            "GET",
            undefined,
            undefined,
            undefined,
            undefined,
            abortSignal,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Get aggregations for medicaid report by active plan type
     */
    static async getMedicaidReportsAggregationActivePlanType(
        practice: IPractice,
        filters: IPoliciesListFiltersData = {},
        abortSignal?: AbortSignal,
    ): Promise<ICoverageHistoryAggregationsMedicaidActivePlanType | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_MEDICAID_GET_AGGREGATION_ACTIVE_PLAN_TYPE.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            {
                ...PoliciesService.mapPoliciesFilters(
                    PoliciesListType.MEDICAID,
                    filters,
                ),
            },
            "GET",
            undefined,
            undefined,
            undefined,
            undefined,
            abortSignal,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Get aggregations for report by status code
     */
    static async getReportsAggregationStatusCodes(
        practice: IPractice,
        filters: IPoliciesListFiltersData = {},
        abortSignal?: AbortSignal,
    ): Promise<ICoverageHistoryAggregationsStatusCodes | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_REPORTS_GET_AGGREGATION_STATUS_CODES.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            {
                ...PoliciesService.mapPoliciesFilters(
                    PoliciesListType.REPORTS,
                    filters,
                ),
            },
            "GET",
            undefined,
            undefined,
            undefined,
            undefined,
            abortSignal,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Get aggregations for report by flags
     */
    static async getReportsAggregationFlags(
        practice: IPractice,
        filters: IPoliciesListFiltersData = {},
        abortSignal?: AbortSignal,
    ): Promise<ICoverageHistoryAggregationsFlags | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_REPORTS_GET_AGGREGATION_FLAGS.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            {
                ...PoliciesService.mapPoliciesFilters(
                    PoliciesListType.REPORTS,
                    filters,
                ),
            },
            "GET",
            undefined,
            undefined,
            undefined,
            undefined,
            abortSignal,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Get aggregations for plan year resets
     */
    static async getPlanYearResetsAggregations(
        practice: IPractice,
        filters: IPoliciesListFiltersData = {},
        abortSignal?: AbortSignal,
    ): Promise<ICoverageHistoryAggregationsPlanYearResets | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_PLAN_YEAR_RESETS_GET_AGGREGATIONS.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            {
                ...PoliciesService.mapPoliciesFilters(
                    PoliciesListType.PLAN_YEAR_RESETS,
                    filters,
                ),
            },
            "GET",
            undefined,
            undefined,
            undefined,
            undefined,
            abortSignal,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Get list of list of policies for agentic reports
     */
    static async getAgenticList(
        practice: IPractice,
        pagination: IPagination,
        filters: IPoliciesListFiltersData = {},
        sort: ICoverageHistoryListSort = {},
        abortSignal?: AbortSignal,
        additionalRequestData: IOneVerifyGetAgenticListAdditionalRequestData = {},
    ): Promise<ICoverageHistoryListResult | undefined> {
        AmplitudeHelper.logEvent(AmplitudeEvent.RAN_REPORT, {
            practiceId: practice.id,
            practiceName: practice.name,
            filterParams: PoliciesHelper.redactSensitiveFiltersData(filters),
            reportType: PoliciesListType.AGENTIC,
        })

        return await PoliciesService.getPoliciesList(
            API_ROUTES.COVERAGE_AGENTIC_REPORTS_GET_LIST,
            practice,
            PoliciesService.mapGetAgenticListRequestData(
                pagination,
                filters,
                sort,
                additionalRequestData,
            ),
            abortSignal,
        )
    }

    /**
     * Get single historical coverage check
     */
    static async getSingle(
        practice: IPractice,
        nirvanaRequestId: string,
        withPolicyDataReFetch?: boolean,
        ignoreToastrErrorCodes: number[] = [],
    ): Promise<IOneVerifyCoverageCheckHistory | undefined> {
        const requestData: IOneVerifyHistoryGetSingleRequestData = {
            withPolicyDataReFetch,
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_HISTORY_GET_SINGLE.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":nirvanaRequestId", nirvanaRequestId),
            requestData,
            undefined,
            undefined,
            undefined,
            undefined,
            ignoreToastrErrorCodes,
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Retry single historical coverage check
     */
    static async retrySingle(
        practice: IPractice,
        nirvanaRequestId: string,
        patientId?: string,
    ): Promise<IOneVerifyCoverageResult | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_HISTORY_RETRY_SINGLE.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":nirvanaRequestId", nirvanaRequestId),
            {
                patientId,
            },
            "POST",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Get single policy related history
     */
    static async getSingleRelatedHistory(
        practice: IPractice,
        nirvanaRequestId: string,
        pagination: IPagination,
    ): Promise<ICoverageHistoryListResult | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_HISTORY_GET_SINGLE_RELATED_HISTORY.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":nirvanaRequestId", nirvanaRequestId),
            {
                start: pagination.start,
                count: pagination.count,
            },
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Write to EHR
     */
    static async writeToEhr(
        practice: IPractice,
        nirvanaRequestId: string,
        requestData: IOneVerifyWriteToEhrData,
    ): Promise<IOneVerifyCoverageCheckHistory | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COVERAGE_WRITE_TO_EHR.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":nirvanaRequestId", nirvanaRequestId),
            requestData,
            "POST",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Map filters for reports, policies, aggregations etc
     */
    static mapPoliciesFilters(
        listType: PoliciesListType,
        filters: IPoliciesListFiltersData = {},
        withObjectStringify = true,
    ): ICoverageHistoryBaseFilters {
        const {
            responseType,
            payer,
            checkDate,
            nextAppointmentDate,
            planEndDate,
            overrideExpirationDate,
            taskAssignmentDateWorked,
            customFieldAppointmentCreatedTimestamp,
            ...restFilters
        } = filters

        return {
            ...(!COMMON_COVERAGE_HISTORY_CONFIG.listTypesNotSupportingHistoricalMode.includes(
                listType,
            )
                ? {
                      historyMode:
                          PoliciesHelper.getHistoryModeBasedOnListType(
                              listType,
                          ),
                  }
                : {}),

            // Convert to array for consistency and don't send empty arrays
            ...(responseType
                ? {
                      responseType: Array.isArray(responseType)
                          ? responseType.length
                              ? responseType
                              : undefined
                          : [responseType],
                  }
                : {}),

            // Make sure not to send empty values or empty arrays
            ...Object.keys(restFilters)
                .filter(key => {
                    const value = restFilters[key as keyof typeof restFilters]

                    // Filter out falsy values and empty arrays
                    return Array.isArray(value) ? value.length > 0 : !!value
                })
                .reduce(
                    (accumulator, key) => ({
                        ...accumulator,
                        [key]: restFilters[key as keyof typeof restFilters],
                    }),
                    {},
                ),

            payerId: payer?.payerId,
            // For get request objects need to be stringified
            // @ts-ignore
            checkDate: checkDate
                ? withObjectStringify
                    ? JSON.stringify(checkDate)
                    : checkDate
                : undefined,
            // For get request objects need to be stringified
            // @ts-ignore
            planEndDate: planEndDate
                ? withObjectStringify
                    ? JSON.stringify(planEndDate)
                    : planEndDate
                : undefined,
            // For get request objects need to be stringified
            // @ts-ignore
            nextAppointmentDate: nextAppointmentDate
                ? withObjectStringify
                    ? JSON.stringify(nextAppointmentDate)
                    : nextAppointmentDate
                : undefined,
            // For get request objects need to be stringified
            // @ts-ignore
            overrideExpirationDate: overrideExpirationDate
                ? withObjectStringify
                    ? JSON.stringify(overrideExpirationDate)
                    : overrideExpirationDate
                : undefined,
            // For get request objects need to be stringified
            // @ts-ignore
            taskAssignmentDateWorked: taskAssignmentDateWorked
                ? withObjectStringify
                    ? JSON.stringify(taskAssignmentDateWorked)
                    : taskAssignmentDateWorked
                : undefined,
            // For get request objects need to be stringified
            // @ts-ignore
            customFieldAppointmentCreatedTimestamp:
                customFieldAppointmentCreatedTimestamp
                    ? withObjectStringify
                        ? JSON.stringify(customFieldAppointmentCreatedTimestamp)
                        : customFieldAppointmentCreatedTimestamp
                    : undefined,
        }
    }

    /**
     * Get policies list - common method for all types of lists
     */
    static async getPoliciesList(
        baseUrl: string,
        practice: IPractice,
        requestData:
            | IOneVerifyGetHistoryListRequestData
            | IOneVerifyGetAgenticListRequestData,
        abortSignal?: AbortSignal,
    ): Promise<ICoverageHistoryListResult | undefined> {
        const result = await HttpHelper.sendRequest(
            baseUrl.replace(":practiceId", practice.id.toString()),
            requestData,
            "GET",
            undefined,
            undefined,
            undefined,
            undefined,
            abortSignal,
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Map request data for get history list
     */
    static mapGetHistoryListRequestData(
        listType: PoliciesListType,
        pagination: IPagination,
        filters: IPoliciesListFiltersData = {},
        sort: ICoverageHistoryListSort = {},
        additionalRequestData: IOneVerifyGetHistoryListAdditionalData = {},
    ): IOneVerifyGetHistoryListRequestData {
        return {
            ...(additionalRequestData || {}),
            ...sort,

            ...PoliciesService.mapPoliciesFilters(listType, filters, undefined),

            start: pagination.start,
            count: pagination.count,
        }
    }

    /**
     * Map request data for get agentic list
     * Same as mapGetHistoryListRequestData, but done separately for typings clarity
     */
    private static mapGetAgenticListRequestData(
        pagination: IPagination,
        filters: IPoliciesListFiltersData = {},
        sort: ICoverageHistoryListSort = {},
        additionalRequestData: IOneVerifyGetAgenticListAdditionalRequestData = {},
    ): IOneVerifyGetAgenticListRequestData {
        return {
            ...(additionalRequestData || {}),
            ...sort,

            ...PoliciesService.mapPoliciesFilters(
                PoliciesListType.AGENTIC,
                filters,
            ),

            start: pagination.start,
            count: pagination.count,
        }
    }
}
